import { useState, useEffect, useRef } from 'react';

/**
 * @param {boolean} dealerSearchEnabled - Whether dealer search is enabled
 * @param {boolean} dealerSearchGeolocationEnabled - Whether dealer search geolocation is enabled
 * @param {boolean} autoConnectedUnsubscribed - Whether auto connected unsubscribed is enabled
 * @param {GeolocationOptions} [options] - Options for geolocation
 * @returns {{loading: boolean, geoError: GeolocationPositionError | null, data: GeolocationCoordinates | {}}}
 */
export default function useGeolocation(
  dealerSearchEnabled: boolean,
  dealerSearchGeolocationEnabled: boolean,
  autoConnectedUnsubscribed: boolean,
  geoLocationOption: 'optIn' | 'optOut' = 'optOut',
  options?: PositionOptions,
) {
  const [loading, setLoading] = useState(false);
  const [geoError, setGeoError] = useState<GeolocationPositionError | null>(null);
  const [data, setData] = useState<GeolocationCoordinates | object>({});

  // Track if geolocation has already been requested to prevent duplicate calls
  const hasRequestedRef = useRef(false);
  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // Only request geolocation if all conditions are met and not already requested
    const shouldRequestGeolocation =
      dealerSearchEnabled &&
      dealerSearchGeolocationEnabled &&
      !autoConnectedUnsubscribed &&
      geoLocationOption !== 'optIn' &&
      !hasRequestedRef.current &&
      typeof navigator !== 'undefined' &&
      'geolocation' in navigator;

    if (!shouldRequestGeolocation) {
      return;
    }

    hasRequestedRef.current = true;
    setLoading(true);

    const successHandler = (position: GeolocationPosition) => {
      if (isMountedRef.current) {
        setLoading(false);
        setGeoError(null);
        setData(position.coords);
      }
    };

    const errorHandler = (error: GeolocationPositionError) => {
      if (isMountedRef.current) {
        setGeoError(error);
        setLoading(false);
      }
    };

    // Only use getCurrentPosition - watchPosition causes continuous re-renders
    // If continuous position tracking is needed, implement it separately with a flag
    navigator.geolocation.getCurrentPosition(successHandler, errorHandler, options);
  }, [
    dealerSearchEnabled,
    dealerSearchGeolocationEnabled,
    autoConnectedUnsubscribed,
    geoLocationOption,
    options,
  ]);

  return { loading, geoError, data };
}
